import Box from '@mui/material/Box';
import type { InputProps } from '@mui/material/Input';
import TextField from '@mui/material/TextField';
import type { ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

type MuiFormInputPropsT = {
  name: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  type?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  icon?: ReactNode;
  InputProps?: Partial<InputProps>;
};

const MuiFormInput = ({
  name,
  label,
  placeholder,
  required = false,
  disabled = false,
  type = 'text',
  onBlur,
  InputProps: inputProps,
}: MuiFormInputPropsT): JSX.Element => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required ? `${label} is required` : false }}
      render={({ field, fieldState }): React.ReactElement => (
        <Box>
          <TextField
            {...field}
            fullWidth
            size="small"
            label={label}
            placeholder={placeholder}
            type={type}
            disabled={disabled}
            required={required}
            error={!!fieldState.error}
            helperText={fieldState.error?.message ?? null}
            onBlur={(e): void => {
              field.onBlur();
              onBlur?.(e as React.FocusEvent<HTMLInputElement>);
            }}
            value={field.value ?? ''}
            InputProps={inputProps}
            sx={(theme): object => ({
              '& .MuiInputBase-input': {
                fontSize: '13px',
                '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus': {
                  WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.paper} inset`,
                  WebkitTextFillColor: theme.palette.text.primary,
                  caretColor: theme.palette.text.primary,
                  transition: 'background-color 5000s ease-in-out 0s',
                },
                ...(type === 'number' && {
                  MozAppearance: 'textfield',
                  '&::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                  '&::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                }),
              },
              '& .MuiInputLabel-root': { fontSize: '13px' },
              '& .MuiFormHelperText-root': { fontSize: '11px' },
            })}
          />
        </Box>
      )}
    />
  );
};

export default MuiFormInput;
